/*
 * Copyright 2022 StarTree Inc.
 *
 * All rights reserved. Confidential and proprietary information of StarTree Inc.
 */
import {
    AppContainerV1,
    getPlatformUiVersionV1,
    NotificationScopeV1,
    NotificationTypeV1,
    useAuthProviderV1,
    useNotificationProviderV1,
} from "@startree-ui/platform-ui";
import { delay } from "lodash";
import React, { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppNavBar } from "./components/app-nav-bar/app-nav-bar.component";
import { useAuthProvider } from "./components/auth-provider-loader/auth-provider-loader.component";
import { AppRouter } from "./routers/app/app.router";
import { getRemoteManagedUiVersion } from "./utils/version/version.util";

export const App: FunctionComponent = () => {
    const { isAuthenticated } = useAuthProvider();
    const [showAppNavBar, setShowAppNavBar] = useState(false);
    const {
        authDisabled,
        authDisabledNotification,
        clearAuthDisabledNotification,
    } = useAuthProviderV1();
    const { notify } = useNotificationProviderV1();
    const { t } = useTranslation();

    useEffect(() => {
        // Slight delay in rendering nav bar helps avoid flicker during initial page redirects
        delay(setShowAppNavBar, 200, isAuthenticated);

        // Log app and platform versions
        console.info(
            `${t("label.remote-managed-ui")}: ${getRemoteManagedUiVersion()}`
        );
        console.info(`${t("label.platform-ui")}: ${getPlatformUiVersionV1()}`);
    }, []);

    useEffect(() => {
        setShowAppNavBar(isAuthenticated);
    }, [isAuthenticated]);

    useEffect(() => {
        if (authDisabled && authDisabledNotification) {
            // Auth disabled and user not yet notified
            notify(
                NotificationTypeV1.Info,
                t("message.authentication-disabled"),
                false,
                NotificationScopeV1.Global,
                clearAuthDisabledNotification
            );
        }
    }, [authDisabled, authDisabledNotification]);

    return (
        <AppContainerV1
            disableDynamicDocumentTitle
            name={t("label.startree-cloud")}
        >
            {showAppNavBar && <AppNavBar />}

            <AppRouter />
        </AppContainerV1>
    );
};
